import {
  BlueBirdTheme,
  DatePicker, Dropdown, Form, TextArea, Typography,
  useSnackbar,
} from "@kitbird/core";
import React from "react";
import { Controller } from "react-hook-form";
import { FormGroup, FormWrapper } from "../../../../components/Form/Form.style";
import { toRupiah, toStringDate } from "../../../../utils/common";
import ModalForm from "../../../../components/Form/ModalForm";
import useCreateReceiptSubmission from "../hooks/useCreateReceiptSubmission";
import validationRules from "../../../../utils/validation";
import { ReactComponent as PaperListIcon } from "../../../../assets/icons/paper_list.svg";
import { ReactComponent as SettingIcon } from "../../../../assets/icons/setting_icon.svg";
import { ReactComponent as CreditCardIcon } from "../../../../assets/icons/credit_card.svg";
import { checkIsRedReceiptAvailable } from "../../../../services/trxRedReceiptService";

interface DetailSubmissionFormI {
  useCustomHook: ReturnType<typeof useCreateReceiptSubmission>,
}

const { gray, primary } = BlueBirdTheme.colors;

const DetailSubmissionForm = ({ useCustomHook }: DetailSubmissionFormI) => {
  const {
    glAccount,
    setGlAccount,
    destinationAccount,
    setDestinationAccount,
    redReceiptNumber,
    setRedReceiptNumber,
    redReceiptType,
    setRedReceiptType,
    redReceiptTypeOpts,
    dataTrxRedReceipt,
    handleSetAmount,
    handleSetPlanPostDate,
    methods,
    trxTypeOpts,
    glAccountOpts,
    vendorAccountOpts,
  } = useCustomHook;
  const {
    control, getValues, resetField, setValue, watch, formState: { errors },
  } = methods;
  const snackbar = useSnackbar();

  const handleRedReceiptChange = async (val: any) => (
    checkIsRedReceiptAvailable(val.trx_id)
      .then((res) => {
        if (res.responseCode === "Succeed") setRedReceiptNumber(val);
        else snackbar.show(res.responseMessage || "Gagal memvalidasi nomor bon merah", "error");
        return res.responseCode === "Succeed";
      })
      .catch(() => {
        snackbar.show("Gagal memvalidasi nomor bon merah", "error");
        return false;
      })
  );

  const isRedReceiptIjir = watch("trxType.value") === "BKI" || watch("trxType.value") === "BKS";

  return (
    <>
      <Typography level={6} color={gray.title} fontWeight="medium">Detail pengajuan</Typography>
      <FormGroup>
        <FormWrapper>
          <Controller
            control={control}
            name="trxType"
            rules={{
              required: validationRules.defaultRequired,
            }}
            render={({ field: { name, onChange, value }, fieldState: { error } }) => (
              <Dropdown
                id={name}
                name={name}
                label="Tipe transaksi"
                options={trxTypeOpts}
                onSelected={onChange}
                value={value?.label}
                error={error && { message: error?.message || "" }}
              />
            )}
          />
        </FormWrapper>
        <FormWrapper>
          <Form
            label="Total bon"
            onChange={handleSetAmount}
            name="amount"
            value={watch("amount") && watch("amount") !== "0"
              ? toRupiah(watch("amount"))
              : ""
            }
            autoComplete="off"
            error={errors.amount && { message: errors.amount?.message || "" }}
          />
        </FormWrapper>
        <FormWrapper>
          <DatePicker
            variant="default"
            label="Estimasi tanggal kirim"
            onChange={handleSetPlanPostDate}
            onReset={() => resetField("planPostDate")}
            onSubmit={handleSetPlanPostDate}
            minDate={new Date()}
            value={watch("planPostDate")}
            error={errors.planPostDate && { message: errors.planPostDate?.message || "" }}
          />
        </FormWrapper>
      </FormGroup>
      {(watch("trxType.value") === "BMH") && <ModalForm
        value={redReceiptType}
        label="Tipe bon merah"
        displayValues={[
          "bm_type",
          "ijirType",
          "ijirExpiryDesc",
        ]}
        options={redReceiptTypeOpts}
        // keyString="label"
        icon={<PaperListIcon color={primary[500]} width={24} height={24} />}
        onChange={setRedReceiptType}
        error={errors.redReceiptType && { message: errors.redReceiptType?.message || "" }}
      />}
      <FormGroup>
        <FormWrapper>
          <ModalForm
            value={glAccount}
            label="Akun GL"
            isLabelStay={isRedReceiptIjir}
            displayValues={[
              "gnlldr",
              "sap_bktxt",
              "trx_desc",
            ]}
            options={glAccountOpts}
            keyString="id"
            icon={<SettingIcon color={primary[500]} width={24} height={24} />}
            onChange={setGlAccount}
            error={errors.trxCode && { message: errors.trxCode?.message || "" }}
          />
        </FormWrapper>
        <FormWrapper>
          {isRedReceiptIjir
            ? <ModalForm
              value={redReceiptNumber}
              label="No. bon merah"
              displayValues={["trx_id_bm", "amountRupiah", "date", "bm_type"]}
              options={dataTrxRedReceipt?.listSmpltrxbm.map((item) => ({
                ...item,
                amountRupiah: toRupiah(item.amount),
                date: toStringDate(item.post_date),
              })) || []}
              keyString="trx_id_bm"
              icon={<PaperListIcon color={primary[500]} width={24} height={24} />}
              error={errors.redReceiptNumber && { message: errors.redReceiptNumber?.message || "" }}
              onChange={handleRedReceiptChange}
            />
            : <ModalForm
              value={destinationAccount}
              label="Rekening tujuan"
              isLabelStay={false}
              displayValues={[
                "bank_account_name",
                "vendor_name",
                "bank_account",
                "bank_account_number",
                "is_withdrawal",
              ]}
              options={vendorAccountOpts}
              keyString="id"
              icon={<CreditCardIcon color={primary[500]} width={24} height={24} />}
              error={errors.beneficiaryCode && { message: errors.beneficiaryCode?.message || "" }}
              onChange={setDestinationAccount}
            />
          }
        </FormWrapper>
      </FormGroup>

      <FormGroup>
        <FormWrapper>
          <TextArea
            label="Deskripsi"
            data-testid="textArea"
            rows={4}
            placeholder="Deskripsi (Contoh : pembelian nasi kotak 100 box)"
            onChange={(e) => {
              const desc = e.target.value.replace(/[^a-zA-Z0-9 ]/g, "");
              setValue("description", desc, { shouldValidate: true });
            }}
            value={getValues("description")}
            error={errors.description && { message: errors.description?.message || "" }}
            maxLength={100}
          />
        </FormWrapper>
      </FormGroup>
    </>
  );
};
export default DetailSubmissionForm;
